// src/pages/Advertisement/ViewAdDetail.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../shared/api/axios';
import ChoshiiLogo from '../ChoshiiLogo';
import { getOverlayForFilter, type FilterPresetId } from '../../shared/components/filterHelpers.web';
import LocationChangeAd from "./LocationChangeAd";
import CalendarModal from "./CalendarModal";
import { CONTINENT_COUNTRIES } from "./CountryChoice";
import SmartVideoPlayerGallery from '../pages/Presentation/SmartVideoPlayerGallery';

const GOLD = '#D4AF37';
const BG = '#050912';
const CARD_BG = '#0D101B';
const BORDER = '#22283a';

// --- Types approximating backend response ---

type DeliveryMode = 'light' | 'balanced' | 'frequent';
type Status = 'draft' | 'running' | 'paused' | 'finished';

type MediaEntry = {
  url?: string;
  kind?: "video" | "image";

  // new pipeline
  isHls?: boolean;
  videoId?: string;
  hlsUrl?: string | null;
  dashUrl?: string | null;
  posterUrl?: string | null;

  // metadata
  musicTitle?: string | null;
  musicArtist?: string | null;
  filterId?: string | null;
};

type BackendMediaDoc = {
  media: MediaEntry[];
  location?: {
    city?: string;
    country?: string;
  };
  hashtags?: string[];
  createdAt?: string;
};

type MaybeDate = Date | string | null | undefined;

type BackendStats = {
  impressionsTotal?: number;
  clicksTotal?: number;
  spendTotal?: number;
  impressionsToday?: number;
  clicksToday?: number;
  spendToday?: number;
};

type BackendTargeting = {
  countries?: string[];
  continents?: string[];
  isWorldwide?: boolean;
  cities?: string[];
  hashtags?: string[];
};

type BackendAd = {
  _id: string;
  name: string;
  status: Status;
  adLabel?: string;
  ctaText?: string;
  clickUrl: string;
  cpm?: number;
  dailyBudget?: number;
  totalBudget?: number;
  startDate?: string | null;
  endDate?: string | null;
  deliveryMode?: DeliveryMode;
  targeting?: BackendTargeting;
  stats?: BackendStats;
  createdAt?: string;
  updatedAt?: string;
  media?: BackendMediaDoc;
};

function toDate(s: string | Date | null | undefined): Date | null {
  if (!s) return null;
  if (s instanceof Date) return isNaN(s.getTime()) ? null : s;

  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}


// --- helper: filter overlay (same as AdOverview) ---
const MEDIA_TILE_WIDTH = 610;

const overlayStyleFor = (fid?: string | null): React.CSSProperties | null => {
  const id = (fid as FilterPresetId) || 'none';
  const { tint, opacity } = getOverlayForFilter(id);
  return tint
    ? {
        position: 'absolute',
        inset: 0,
        backgroundColor: tint,
        opacity,
        pointerEvents: 'none',
        borderRadius: 12,
      }
    : null;
};


const API_BASE = process.env.REACT_APP_API_BASE_URL as string;

if (!API_BASE) {
  throw new Error("REACT_APP_API_BASE_URL is not defined");
}

const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, '').replace(/\/$/, '');


const resolveVideoUrl = (m: MediaEntry): string => {
  if (m.hlsUrl) return m.hlsUrl;
  if (m.videoId) return `${API_ORIGIN}/hls/playlist/${m.videoId}`;
  if (m.url) return m.url; // legacy fallback
  return "";
};


const isVideo = (m: MediaEntry): boolean => {
  if (m.kind === "video") return true;
  if (m.isHls) return true;
  if (m.videoId) return true;
  if (m.hlsUrl) return true;
  if (!m.url) return false;

  return (
    /\.(mp4|webm|mov|m4v)$/i.test(m.url) ||
    /\.m3u8(\?|$)/i.test(m.url) ||
    /\/hls\/playlist\//i.test(m.url)
  );
};


const ViewAdDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [ad, setAd] = useState<BackendAd | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<boolean>(false);
  // --- inline edit states ---
  const [editDailyBudget, setEditDailyBudget] = useState(false);
  const [editTotalBudget, setEditTotalBudget] = useState(false);
  const [editDelivery, setEditDelivery] = useState(false);
  const [editStartEnd, setEditStartEnd] = useState(false);

  const [draftDailyBudget, setDraftDailyBudget] = useState<number>(0);
  const [draftTotalBudget, setDraftTotalBudget] = useState<number | ''>('');
  const [draftDeliveryMode, setDraftDeliveryMode] = useState<DeliveryMode>('balanced');
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  const [draftStartDate, setDraftStartDate] = useState<Date | null>(null);
const [draftEndDate, setDraftEndDate] = useState<Date | null>(null);

const [calendarOpen, setCalendarOpen] = useState(false);
const [activeField, setActiveField] = useState<"start" | "end">("start");

const [editStart, setEditStart] = useState(false);
const [editEnd, setEditEnd] = useState(false);

const startInputRef = React.useRef<HTMLInputElement | null>(null);
const endInputRef = React.useRef<HTMLInputElement | null>(null);
const startBtnRef = React.useRef<HTMLButtonElement | null>(null);
const endBtnRef = React.useRef<HTMLButtonElement | null>(null);


const ALL_COUNTRIES = useMemo(() => {
  return Object.values(CONTINENT_COUNTRIES)
    .flat()
    .filter((c, i, arr) => arr.findIndex((x) => x.code === c.code) === i)
    .sort((a, b) => a.name.localeCompare(b.name));
}, []);

const COUNTRY_CODE_TO_NAME = useMemo(() => {
  return new Map(ALL_COUNTRIES.map((c) => [c.code, c.name]));
}, [ALL_COUNTRIES]);

const toDateSafe = (s?: MaybeDate): Date | null => {
  if (!s) return null;

  if (s instanceof Date) {
    return Number.isNaN(s.getTime()) ? null : s;
  }

  const raw = String(s).trim();

  // yyyy-mm-dd (treat as UTC midnight to avoid timezone shifts)
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = new Date(`${raw}T00:00:00.000Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // "yyyy-mm-dd hh:mm:ss"
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(raw)) {
    const d = new Date(raw.replace(' ', 'T') + 'Z');
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const sameDay = (a: Date | null, b: Date | null) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return startOfDay(a).getTime() === startOfDay(b).getTime();
};

const formatISO = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : '');



const parseBackendDate = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;

  const raw = String(v).trim();

  // yyyy-mm-dd (treat as UTC midnight to avoid timezone shifts)
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = new Date(`${raw}T00:00:00.000Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // "yyyy-mm-dd hh:mm:ss" (convert to ISO-like UTC)
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/.test(raw)) {
    const d = new Date(raw.replace(' ', 'T') + 'Z');
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const adStart = toDateSafe(ad?.startDate);
const adEnd = toDateSafe(ad?.endDate);

const today = startOfDay(new Date());
const startedAlready = adStart ? startOfDay(adStart).getTime() < today.getTime() : false;
const finished = adEnd ? startOfDay(adEnd).getTime() < today.getTime() : false;

const startValue = draftStartDate ?? adStart; // ✅ always show start if it exists on ad
const endValue = draftEndDate ?? adEnd;

const endedAlready = adEnd ? startOfDay(adEnd).getTime() < today.getTime() : false;

  
const isFinished = ad?.status === 'finished';
const canEditStart = !isFinished && !startedAlready; // if started: show date but no Change button
const canEditEnd = !isFinished && !endedAlready;     // if ended: no Change button
 // Always show what the backend has, even if drafts are temporarily null
const endDateDisplayRaw = formatISO(draftEndDate ?? adEnd);

const isScheduleDirty =
!sameDay(draftStartDate ?? adStart, adStart) || !sameDay(draftEndDate ?? adEnd, adEnd);

// Use your existing display formatting (these are placeholders)
const startDateDisplay = startValue ? startValue.toLocaleDateString() : '—';
const endDateDisplay = endValue ? endValue.toLocaleDateString() : '—';

const activeAnchorRef = activeField === 'start' ? startBtnRef : endBtnRef;

// Call your backend save here (or keep it local for now)
const saveSchedule = async () => {
  if (!ad) return;

  // enforce end >= start (if both exist)
  let nextStart = draftStartDate ?? adStart;
  let nextEnd = draftEndDate ?? adEnd;

  if (nextStart && nextEnd && startOfDay(nextEnd).getTime() < startOfDay(nextStart).getTime()) {
    nextEnd = nextStart;
  }

  await patchAd({
    startDate: nextStart ? nextStart.toISOString() : null,
    endDate: nextEnd ? nextEnd.toISOString() : null,
  });

  // after patchAd -> it re-fetches and sets `ad`,
  // and your [ad] effect will reset drafts to the backend values -> not dirty anymore -> page unblocks
  setCalendarOpen(false);
};

const resetSchedule = () => {
  // reset drafts back to what backend currently has
  setDraftStartDate(adStart);
  setDraftEndDate(adEnd);
  setCalendarOpen(false);
};




  // set drafts after ad loads
  useEffect(() => {
    if (!ad) return;
    setDraftDailyBudget(ad.dailyBudget ?? 0);
    setDraftTotalBudget(ad.totalBudget && ad.totalBudget > 0 ? ad.totalBudget : '');
    setDraftDeliveryMode(ad.deliveryMode ?? 'balanced');

    // backend likely stores ISO strings; convert to yyyy-mm-dd for <input type="date">
    setDraftStartDate(toDateSafe(ad.startDate) ?? null);
    setDraftEndDate(toDateSafe(ad.endDate) ?? null);

  }, [ad]);

  
  
  const saveStartDate = async () => {
    if (!ad) return;
  
    const startedAlready =
      ad?.startDate
        ? startOfDay(new Date(ad.startDate)).getTime() < startOfDay(new Date()).getTime()
        : false;
  
    if (startedAlready && formatISO(draftStartDate) !== formatISO(ad.startDate ? new Date(ad.startDate) : null)) {
      alert(t('ad.viewDetail.alerts.startDateInPast', 'Start date is already in the past and cannot be changed.'));
      return;
    }
  
    // keep end >= start
    let nextEnd = draftEndDate;
    if (draftStartDate && nextEnd && startOfDay(nextEnd).getTime() < startOfDay(draftStartDate).getTime()) {
      nextEnd = draftStartDate;
      setDraftEndDate(nextEnd);
    }
  
    await patchAd({
      startDate: draftStartDate ? draftStartDate.toISOString() : null,
      endDate: nextEnd ? nextEnd.toISOString() : null,
    });
  
    setEditStart(false);
  };
  
  const saveEndDate = async () => {
    if (!ad) return;
  
    if (draftStartDate && draftEndDate && startOfDay(draftEndDate).getTime() < startOfDay(draftStartDate).getTime()) {
      alert(t('ad.viewDetail.alerts.endBeforeStart', 'End date cannot be before start date.'));
      return;
    }
  
    await patchAd({ endDate: draftEndDate ? draftEndDate.toISOString() : null });
    setEditEnd(false);
  };
  
  const patchAd = async (payload: any) => {
    if (!id) return;
    const res = await api.patch(`/ads/${id}`, payload);
    // safest: re-fetch current ad after patch
    const fresh = await api.get(`/ads/${id}`);
    setAd(fresh.data as BackendAd);
  };

  const todayISO = new Date().toISOString().slice(0, 10);

  // Load ad detail
  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/ads/${id}`);
        if (cancelled) return;
        setAd(res.data as BackendAd);
      } catch (err) {
        console.error('[ViewAdDetail] Failed to load ad', err);
        if (!cancelled) setError('Failed to load advertisement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleStatusChange = async (status: Status) => {
    if (!id || !ad) return;
    // Confirm for finishing
    if (status === 'finished') {
      const ok = window.confirm(t('ad.viewDetail.confirm.endCampaign', 'End this campaign? It will stop delivering.'));
      if (!ok) return;
    }

    try {
      setUpdatingStatus(true);
      await api.patch(
        `/ads/${id}`,
        { status }
      );
      
      setAd((prev) => (prev ? { ...prev, status } : prev));
    } catch (err) {
      console.error('[ViewAdDetail] Failed to update status', err);
      alert(t('ad.viewDetail.alerts.updateStatusFailed', 'Failed to update campaign status. Please try again.'));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const stats = ad?.stats || {};
  const impressions = stats.impressionsTotal ?? 0;
  const clicks = stats.clicksTotal ?? 0;
  const spendTotal = stats.spendTotal ?? 0;

  const ctr = useMemo(() => {
    if (!impressions) return 0;
    return (clicks / impressions) * 100;
  }, [impressions, clicks]);

  const effectiveCpm = useMemo(() => {
    if (!impressions) return 0;
    return (spendTotal / impressions) * 1000;
  }, [spendTotal, impressions]);

  const mediaEntries: MediaEntry[] = ad?.media?.media || [];

const targetingSummary = useMemo(() => {
  if (!ad?.targeting) return t('ad.viewDetail.destination.dash', '—');

  const tgt = ad.targeting;

  if (tgt.isWorldwide) return t('ad.viewDetail.targeting.worldwide', 'Worldwide');

  const parts: string[] = [];

  if (tgt.continents && tgt.continents.length) {
    parts.push(
      t('ad.viewDetail.targeting.continents', 'Continents: {{list}}', {
        list: tgt.continents.join(', '),
      })
    );
  }

  if (tgt.countries && tgt.countries.length) {
    parts.push(
      t('ad.viewDetail.targeting.countries', 'Countries: {{list}}', {
        list: tgt.countries
        .map((code) => COUNTRY_CODE_TO_NAME.get(code) ?? code)
        .join(", "),
    })
    );
  }

  if (tgt.cities && tgt.cities.length) {
    parts.push(
      t('ad.viewDetail.targeting.cities', 'Cities: {{list}}', {
        list: tgt.cities.join(', '),
      })
    );
  }

  return parts.length
    ? parts.join(' · ')
    : t('ad.viewDetail.targeting.none', 'No specific targeting');
}, [ad?.targeting, t]);


  const statusPill = (status: Status) => {
    let bg = 'rgba(148,163,184,0.16)';
    let color = '#e5e7eb';

    if (status === 'running') {
      bg = 'rgba(34,197,94,0.16)';
      color = '#4ade80';
    } else if (status === 'paused') {
      bg = 'rgba(234,179,8,0.16)';
      color = '#facc15';
    } else if (status === 'draft') {
      bg = 'rgba(59,130,246,0.16)';
      color = '#93c5fd';
    }

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 10px',
          borderRadius: 999,
          fontSize: 11,
          background: bg,
          color,
        }}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: BG,
          color: '#e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
{t('ad.viewDetail.loading', 'Loading campaign…')}
</div>
    );
  }

  if (error || !ad) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: BG,
          color: '#e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div>{error || t('ad.viewDetail.errors.notFound', 'Campaign not found.')}</div>
        <button
          onClick={() => navigate('/advertisementstart')}
          style={{
            borderRadius: 999,
            border: `1px solid ${BORDER}`,
            background: 'transparent',
            color: '#e5e7eb',
            fontSize: 13,
            padding: '6px 14px',
            cursor: 'pointer',
          }}
        >
{t('ad.viewDetail.buttons.backToOverview', 'Back to overview')}
</button>
      </div>
    );
  }

  const trimmedUrl = (ad.clickUrl || '').trim();
  const hasValidUrl = /^https?:\/\//i.test(trimmedUrl);

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.headerLeft}
           onClick={() => navigate('/advertisementstart')}
           >
            <ChoshiiLogo    
              animateSheen
              fontFamily="'Playfair Display', serif"
              style={{ width: 140 }}
              padding={0}
            />
            <div>
              <div style={styles.titleRow}>
                <h1 style={styles.h1}>{ad.name}</h1>
                {statusPill(ad.status)}
              </div>
  
              <div style={styles.mutedMeta}>
                {t('ad.viewDetail.header.campaignId', 'Campaign ID: {{id}}', { id: ad._id })}
              </div>
            </div>
          </div>
  
          <div style={styles.headerButtons}>
            <button onClick={() => navigate('/advertisementstart')} style={styles.pillButton}>
              ← {t('ad.viewDetail.buttons.back', 'Back')}
            </button>
  
            {ad.status === 'running' && (
              <button
                disabled={updatingStatus}
                onClick={() => handleStatusChange('paused')}
                style={styles.smallPillButtonWarn}
              >
                {t('ad.viewDetail.buttons.pause', 'Pause')}
              </button>
            )}
  
            {ad.status === 'paused' && (
              <button
                disabled={updatingStatus}
                onClick={() => handleStatusChange('running')}
                style={styles.smallPillButtonOk}
              >
                {t('ad.viewDetail.buttons.resume', 'Resume')}
              </button>
            )}
  
            {ad.status !== 'finished' && (
              <button
                disabled={updatingStatus}
                onClick={() => handleStatusChange('finished')}
                style={styles.smallPillButtonDanger}
              >
                {t('ad.viewDetail.buttons.endCampaign', 'End campaign')}
              </button>
            )}
          </div>
        </header>
  
        {/* Layout grid */}
        <div style={styles.grid}>
          {/* LEFT */}
          <section style={styles.leftCol}>
            {/* Performance */}
            <div style={styles.card}>
              <h2 style={styles.h2}>
                {t('ad.viewDetail.sections.performance', 'Performance')}
              </h2>
  
              <div style={styles.metricsGrid}>
                <div>
                  <div style={styles.mutedLabel}>
                    {t('ad.viewDetail.performance.impressions', 'Impressions')}
                  </div>
                  <div style={styles.metricValueLg}>{impressions.toLocaleString('en-US')}</div>
                </div>
  
                <div>
                  <div style={styles.mutedLabel}>
                    {t('ad.viewDetail.performance.clicks', 'Clicks')}
                  </div>
                  <div style={styles.metricValueLg}>{clicks.toLocaleString('en-US')}</div>
                </div>
  
                <div>
                  <div style={styles.mutedLabel}>
                    {t('ad.viewDetail.performance.ctr', 'CTR')}
                  </div>
                  <div style={styles.metricValueLg}>{ctr.toFixed(2)}%</div>
                </div>
              </div>
  
              <div style={styles.metricsGridSmall}>
                <div>
                  <div style={styles.mutedLabel}>
                    {t('ad.viewDetail.performance.spendTotal', 'Spend total')}
                  </div>
                  <div style={styles.metricValueMd}>€{spendTotal.toFixed(2)}</div>
                </div>
  
                <div>
                  <div style={styles.mutedLabel}>
                    {t('ad.viewDetail.performance.cpmEffective', 'CPM (effective)')}
                  </div>
                  <div style={styles.metricValueMd}>€{effectiveCpm.toFixed(2)}</div>
                </div>
  
                <div>
                  <div style={styles.mutedLabel}>
                    {t('ad.viewDetail.performance.cpmConfigured', 'CPM (configured)')}
                  </div>
                  <div style={styles.metricValueMd}>€{(ad.cpm ?? 0).toFixed(2)}</div>
                </div>
              </div>
            </div>
  
            {/* Budget & delivery */}
            <div style={styles.card}>
              <div style={styles.cardHeaderRow}>
                <h2 style={styles.cardTitle}>
                  {t('ad.viewDetail.sections.budgetDelivery', 'Budget & delivery')}
                </h2>
              </div>
  
              {/* Daily + Total */}
              <div style={styles.twoColGrid}>
                {/* Daily budget */}
                <div>
                  <div style={styles.fieldHeaderRow}>
                    <div style={styles.mutedbdLabel}>{t('ad.viewDetail.budget.dailyBudget', 'Daily budget')}
  
                    {!editDailyBudget ? (
                      <button type="button" onClick={() => setEditDailyBudget(true)} style={{...styles.smallPillButton, marginLeft: 15}}>
                        {t('common.change', 'Change')}
                      </button>
                    ) : null}
                  </div>
                  </div>
                  {!editDailyBudget ? (
                    <div style={styles.metricValueMd}>€{(ad.dailyBudget ?? 0).toFixed(2)}</div>
                  ) : (
                    <div style={styles.inlineEditorRow}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={String(draftDailyBudget)}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^\d]/g, '');
                          setDraftDailyBudget(raw === '' ? 0 : Number(raw));
                        }}
                        style={styles.inputPill}
                      />
  
                      <button
                        type="button"
                        onClick={async () => {
                          await patchAd({ dailyBudget: draftDailyBudget });
                          setEditDailyBudget(false);
                        }}
                        style={styles.saveBtn}
                      >
                        {t('ad.viewDetail.actions.save', 'Save')}
                      </button>
  
                      <button
                        type="button"
                        onClick={() => {
                          setEditDailyBudget(false);
                          setDraftDailyBudget(ad.dailyBudget ?? 0);
                        }}
                        style={styles.cancelBtn}
                      >
                        {t('ad.viewDetail.actions.cancel', 'Cancel')}
                      </button>
                    </div>
                  )}
                </div>
  
                {/* Total budget */}
                <div>
                  <div style={styles.fieldHeaderRow}>
                    <div style={styles.mutedbdLabel}>{t('ad.viewDetail.budget.totalBudget', 'Total budget')}
  
                    {!editTotalBudget ? (
                      <button type="button" onClick={() => setEditTotalBudget(true)} style={{...styles.smallPillButton, marginLeft: 15,}}>
                        {t('common.change', 'Change')}
                      </button>
                    ) : null}
                  </div>
                  </div>
                  {!editTotalBudget ? (
                    <div style={styles.metricValueMd}>
                      {ad.totalBudget && ad.totalBudget > 0
                        ? `€${ad.totalBudget.toFixed(2)}`
                        : t('ad.viewDetail.budget.unlimited', 'Unlimited')}
                    </div>
                  ) : (
                    <div style={styles.inlineEditorRow}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={draftTotalBudget === '' ? '' : String(draftTotalBudget)}
                        placeholder={t('ad.viewDetail.budget.unlimited', 'Unlimited')}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^\d]/g, '');
                          setDraftTotalBudget(raw === '' ? '' : Number(raw));
                        }}
                        style={styles.inputPill}
                      />
  
                      <button
                        type="button"
                        onClick={async () => {
                          await patchAd({ totalBudget: draftTotalBudget === '' ? 0 : draftTotalBudget });
                          setEditTotalBudget(false);
                        }}
                        style={styles.saveBtn}
                      >
                        {t('ad.viewDetail.actions.save', 'Save')}
                      </button>
  
                      <button
                        type="button"
                        onClick={() => {
                          setEditTotalBudget(false);
                          setDraftTotalBudget(ad.totalBudget && ad.totalBudget > 0 ? ad.totalBudget : '');
                        }}
                        style={styles.cancelBtn}
                      >
                        {t('ad.viewDetail.actions.cancel', 'Cancel')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
  
{/* Schedule (Start/End) */}
<div style={{ ...styles.blockTop, position: 'relative' }}>
  {/* BLOCK WHOLE PAGE WHEN DIRTY */}
  {isScheduleDirty ? <div style={styles.pageBlocker} /> : null}

  {/* Keep schedule UI above the blocker */}
  <div style={styles.scheduleWrap}>
    {/* LEFT: Start */}
    <div style={styles.scheduleCol}>
      <div style={styles.scheduleHeaderRow}>
        <div style={styles.mutedbdLabel}>
          {t('ad.viewDetail.budget.startDate', 'Start date')}
          {canEditStart ? (

        <button
          ref={startBtnRef}
          type="button"
          onClick={() => {
            setActiveField('start');
            setCalendarOpen(true);
          }}
          style={{...styles.smallPillButton, marginLeft: 15,}}
        >
          {t('common.change', 'Change')}
        </button>
         ) : null}
      </div>
      </div>

      <div style={styles.valueText}>{startDateDisplay || '—'}</div>
    </div>

    {/* MIDDLE: End */}
    <div style={styles.scheduleCol}>
      <div style={styles.scheduleHeaderRow}>
        <div style={styles.mutedbdLabel}>
          {t('ad.viewDetail.budget.endDate', 'End date')}
          {canEditEnd ? (
        <button
          ref={endBtnRef}
          type="button"
          onClick={() => {
            setActiveField('end');
            setCalendarOpen(true);
          }}
          style={{...styles.smallPillButton, marginLeft: 15,}}
        >
          {t('common.change', 'Change')}
        </button>
        ) : null}
      </div>
      </div>

      <div style={styles.valueText}>{endDateDisplay || '—'}</div>
    </div>

    {/* RIGHT: One action stack for BOTH fields */}
    {isScheduleDirty ? (
      <div style={styles.scheduleActionsCol}>
        <button type="button" onClick={saveSchedule} style={styles.saveBtn}>
          {t('ad.viewDetail.actions.save', 'Save Date')}
        </button>
        <button type="button" onClick={resetSchedule} style={styles.cancelBtn}>
          {t('common.reset', 'Reset Date')}
        </button>
      </div>
    ) : null}
  </div>

  <CalendarModal
    open={calendarOpen}
    onOpenChange={setCalendarOpen}
    title=""
    value={activeField === 'start' ? draftStartDate : draftEndDate}
    anchorRef={activeAnchorRef}
    minDate={activeField === 'start' ? new Date() : draftStartDate ?? new Date()}
    onSelect={(date) => {
      if (!date) return;

      if (activeField === 'start') {
        // if cannot edit start, just ignore
        if (!canEditStart) return;

        setDraftStartDate(date);

        // keep end >= start
        if (draftEndDate && startOfDay(draftEndDate).getTime() < startOfDay(date).getTime()) {
          setDraftEndDate(date);
        }

        setCalendarOpen(false);
        return;
      }

      // end
      if (!canEditEnd) return;

      if (draftStartDate && startOfDay(date).getTime() < startOfDay(draftStartDate).getTime()) {
        alert(t('ad.viewDetail.alerts.endBeforeStart', 'End date cannot be before start date.'));
        return;
      }

      setDraftEndDate(date);
      setCalendarOpen(false);
    }}
  />
</div>



              {/* Delivery */}
              <div style={styles.blockTop}>
                <div style={styles.fieldHeaderRow}>
                  <div style={styles.mutedbdLabel}>{t('ad.viewDetail.budget.delivery', 'Delivery Mode')}
  
                  {!editDelivery ? (
                    <button type="button" onClick={() => setEditDelivery(true)} style={{...styles.smallPillButton, marginLeft: 15,}}>
                      {t('common.change', 'Change')}
                    </button>
                  ) : null}
                </div>
                </div>
                {!editDelivery ? (
                  <div style={styles.bodyText}>
                    {ad.deliveryMode === 'light' &&
                      t('ad.viewDetail.budget.deliveryValues.light', 'Light (show less often)')}
                    {ad.deliveryMode === 'balanced' && t('ad.viewDetail.budget.deliveryValues.balanced', 'Balanced')}
                    {ad.deliveryMode === 'frequent' &&
                      t('ad.viewDetail.budget.deliveryValues.frequent', 'Frequent (show more often)')}
                  </div>
                ) : (
                  <div style={styles.inlineEditorRow}>
                    <select
                      value={draftDeliveryMode}
                      onChange={(e) => setDraftDeliveryMode(e.target.value as DeliveryMode)}
                      style={styles.selectPill}
                    >
                      <option value="light">Light</option>
                      <option value="balanced">Balanced</option>
                      <option value="frequent">Frequent</option>
                    </select>
  
                    <button
                      type="button"
                      onClick={async () => {
                        await patchAd({ deliveryMode: draftDeliveryMode });
                        setEditDelivery(false);
                      }}
                      style={styles.saveBtn}
                    >
                      {t('ad.viewDetail.actions.save', 'Save')}
                    </button>
  
                    <button
                      type="button"
                      onClick={() => {
                        setEditDelivery(false);
                        setDraftDeliveryMode(ad.deliveryMode ?? 'balanced');
                      }}
                      style={styles.cancelBtn}
                    >
                      {t('ad.viewDetail.actions.cancel', 'Cancel')}
                    </button>
                  </div>
                )}
              </div>

            </div>
  
  
            {/* Targeting */}
            <div style={styles.card}>
              <div style={styles.cardHeaderRow}>
                <h2 style={styles.cardTitle}>
                  {t('ad.viewDetail.sections.targeting', 'Targeting')}
                </h2>
  
                <button
                  type="button"
                  onClick={() => setLocationModalOpen(true)}
                  style={styles.smallPillButton}
                >
                  {t('common.change', 'Change')}
                </button>
              </div>
  
              <div style={styles.mutedLabel}>
                {t('ad.viewDetail.targeting.summary', 'Summary')}
              </div>
              <div style={styles.bodyText}>{targetingSummary}</div>
            </div>
  
            {/* Destination */}
            <div style={styles.card}>
              <h2 style={styles.h2}>
                {t('ad.viewDetail.sections.destination', 'Destination')}
              </h2>
  
              <div style={styles.mutedLabel}>{t('ad.viewDetail.destination.urlLabel', 'URL')}</div>
              <div style={styles.urlRow}>
                {hasValidUrl ? (
                  <a href={trimmedUrl} target="_blank" rel="noopener noreferrer" style={styles.link}>
                    {trimmedUrl}
                  </a>
                ) : (
                  trimmedUrl || '—'
                )}
              </div>
  
              <div style={styles.mutedLabel}>{t('ad.viewDetail.destination.buttonLabel', 'Button label')}</div>
              <div style={styles.bodyText}>
                {ad.ctaText || t('ad.viewDetail.preview.visitWebsiteFallback', 'Visit website')}
              </div>
            </div>
          </section>
  
          {/* RIGHT */}
          <section style={styles.rightColCard}>
            <h2 style={styles.h2}>{t('ad.viewDetail.sections.adPreview', 'Ad preview')}</h2>
  
            <div style={styles.previewHeaderRow}>
              <div>
                <div style={styles.previewTitle}>
                  {ad.name || t('ad.viewDetail.preview.adCampaignFallback', 'Ad campaign')}
                </div>
                <div style={styles.previewSub}>
                  {ad.adLabel || t('ad.viewDetail.preview.sponsoredFallback', 'Sponsored')}
                </div>
              </div>
  
              {hasValidUrl ? (
                <a href={trimmedUrl} target="_blank" rel="noopener noreferrer" style={styles.visitWebsite}>
                  {ad.ctaText || t('ad.viewDetail.preview.visitWebsiteFallback', 'Visit website')}
                </a>
              ) : (
                <button type="button" style={styles.visitWebsiteDisabled} disabled>
                  {ad.ctaText || t('ad.viewDetail.preview.visitWebsiteFallback', 'Visit website')}
                </button>
              )}
            </div>
  
            <div style={styles.mediaList}>
              {mediaEntries.length === 0 && (
                <div style={styles.emptyText}>
                  {t('ad.viewDetail.preview.noMediaAttached', 'No media attached to this ad.')}
                </div>
              )}
  
              {mediaEntries.map((m, index) => {
                const overlay = overlayStyleFor(m.filterId ?? null);
                const video = isVideo(m);
const url = video ? resolveVideoUrl(m) : (m.url || "");
if (!url) return null;
  
                return (
                  <div key={`${url}-${index}`} style={styles.mediaItemWrap}>
                    {video ? (
  <SmartVideoPlayerGallery
    videoUrl={url}
    muted
    style={styles.mediaVideo}
  />
) : (

                     
                 
                      <div style={styles.mediaTile}>
                        <img
                          src={url}
                          alt={`ad-media-${index}`}
                          loading="lazy"
                          style={styles.mediaImg}
                        />
                        {overlay && <div style={{ ...overlay, borderRadius: 12 }} />}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </main>
  
      <LocationChangeAd
        open={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        initialTargeting={ad.targeting ?? null}
        onSave={async (next) => {
          if (!id) return;
          await patchAd({ targeting: next });
          setLocationModalOpen(false);
        }}
        BORDER={BORDER}
        CARD_BG={CARD_BG}
      />
    </div>
  );  
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: BG,
    color: '#eee',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  main: {
    padding: '24px 32px',
    width: '100%',
    boxSizing: 'border-box',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 16,
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    cursor: 'pointer',

  },

  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },

  h1: { margin: 0, fontSize: 22, fontWeight: 600 },

  mutedMeta: { fontSize: 12, color: '#9ca3af' },

  headerButtons: { display: 'flex', gap: 8, flexWrap: 'wrap' },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1.5fr)',
    gap: 20,
  },

  leftCol: { display: 'flex', flexDirection: 'column', gap: 16 },

  card: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: 16,
    boxShadow: '0 16px 30px rgba(0,0,0,0.35)',
  },

  cardHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    gap: 12,
  },

  cardTitle: { margin: 0, fontSize: 16, fontWeight: 600 },

  h2: { margin: 0, marginBottom: 10, fontSize: 16, fontWeight: 600 },

  mutedLabel: { fontSize: 14, color: '#9ca3af', },
  mutedbdLabel: { fontSize: 14, color: '#9ca3af', marginBottom: -6, marginTop: 5, },

  bodyText: { fontSize: 13, lineHeight: 1.45 },

  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
  },

  metricsGridSmall: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
    marginTop: 12,
  },

  metricValueLg: { fontSize: 14, marginTop: 2, fontWeight: 600 },
  metricValueMd: { fontSize: 13, fontWeight: 600 },

  twoColGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
  },

  fieldHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    marginBottom: 6,
  },

  inlineEditorRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },

  inlineEditorRowCompact: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },

  blockTop: { marginTop: 24 },

  inputPill: {
    backgroundColor: '#020617',
    borderRadius: 999,
    border: `1px solid rgba(148,163,184,0.8)`,
    padding: '8px 10px',
    color: '#e5e7eb',
    fontSize: 13,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    cursor: 'pointer',
  },

  selectPill: {
    backgroundColor: '#020617',
    borderRadius: 999,
    border: `1px solid rgba(148,163,184,0.8)`,
    padding: '8px 10px',
    color: '#e5e7eb',
    fontSize: 13,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },

  pillButton: {
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    background: 'transparent',
    color: '#e5e7eb',
    fontSize: 12,
    padding: '6px 12px',
    cursor: 'pointer',
  },

  smallPillButton: {
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    background: 'transparent',
    color: '#e5e7eb',
    fontSize: 11,
    padding: '4px 10px',
    cursor: 'pointer',
  },

  smallPillButtonOk: {
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    background: 'transparent',
    color: '#4ade80',
    fontSize: 12,
    padding: '6px 12px',
    cursor: 'pointer',
  },

  smallPillButtonWarn: {
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    background: 'transparent',
    color: '#f97316',
    fontSize: 12,
    padding: '6px 12px',
    cursor: 'pointer',
  },

  smallPillButtonDanger: {
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    background: 'transparent',
    color: '#f87171',
    fontSize: 12,
    padding: '6px 12px',
    cursor: 'pointer',
  },

  scheduleWrap: {
    position: 'relative',
    zIndex: 2, // above the blocker
    display: 'flex',
    alignItems: 'flex-start',
    gap: 24,
  },
  
  scheduleCol: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  
  scheduleHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  
  scheduleActionsCol: {
    marginLeft: 30,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    alignItems: 'flex-end',
  },
  
  pageBlocker: {
    position: 'fixed',
    inset: 0,
    zIndex: 1,
    background: 'rgba(0,0,0,0.28)',   // ✅ dims rest of page lightly
    backdropFilter: 'blur(1px)',      // ✅ optional: subtle softness
    pointerEvents: 'auto',            // keeps the “no actions” behavior
    transition: 'background 160ms ease, backdrop-filter 160ms ease',
  },
  
  
  twoColRow: {
    display: 'flex',
    gap: 24,
  },
  
  col: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },

  saveBtn: {
    borderRadius: 999,
    border: `1px solid ${GOLD}`,
    background: 'transparent',
    color: GOLD,
    fontSize: 12,
    padding: '6px 12px',
    cursor: 'pointer',
  },

  cancelBtn: {
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    background: 'transparent',
    color: '#e5e7eb',
    fontSize: 12,
    padding: '6px 12px',
    cursor: 'pointer',
  },

  urlRow: { fontSize: 13, wordBreak: 'break-all', marginBottom: 10 },

  link: { color: '#93c5fd', textDecoration: 'none' },

  rightColCard: {
    background: CARD_BG,
    border: `1px solid ${BORDER}`,
    borderRadius: 16,
    padding: 16,
    boxShadow: '0 16px 30px rgba(0,0,0,0.35)',
  },

  previewHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    gap: 12,
  },

  previewTitle: { fontWeight: 600, fontSize: 14 },
  previewSub: { fontSize: 12, opacity: 0.7 },

  visitWebsite: {
    padding: '6px 12px',
    borderRadius: 999,
    border: `1px solid ${GOLD}`,
    background: 'radial-gradient(circle at top left, #fceabb 0%, #f8b500 45%, #b88700 100%)',
    color: '#111',
    fontWeight: 600,
    fontSize: 12,
    textDecoration: 'none',
    boxShadow: '0 0 18px rgba(212, 175, 55, 0.35)',
  },

  visitWebsiteDisabled: {
    padding: '6px 12px',
    borderRadius: 999,
    border: `1px solid ${BORDER}`,
    background: 'transparent',
    color: '#9ca3af',
    fontWeight: 500,
    fontSize: 12,
    cursor: 'not-allowed',
  },

  mediaList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    maxHeight: '82vh',
    overflowY: 'auto',
    paddingRight: 4,
  },

  emptyText: { fontSize: 13, opacity: 0.7 },

  mediaItemWrap: { position: 'relative' },

  mediaTile: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },

  mediaVideo: {
    display: 'block',
    maxWidth: MEDIA_TILE_WIDTH,
    maxHeight: MEDIA_TILE_WIDTH,
    width: '100%',
    borderRadius: 12,
  },

  valueText: {
    fontSize: 13,
    fontWeight: 600,
    marginTop: 2,
  },

  mediaImg: {
    display: 'block',
    maxWidth: MEDIA_TILE_WIDTH,
    maxHeight: MEDIA_TILE_WIDTH,
    width: '100%',
    objectFit: 'contain',
    objectPosition: 'center',
    borderRadius: 12,
  },
};


export default ViewAdDetail;
